import { useCallback, useEffect, useState } from "react";
import { ApiError, type OnboardingState, type Step, api, getToken, setToken } from "./lib/api";
import { go, useRoute } from "./lib/router";
import { Notice, Shell, Steps } from "./components/Shell";
import { AgreementsStep } from "./screens/AgreementsStep";
import { Home } from "./screens/Home";
import { KycStep } from "./screens/KycStep";
import { PlanStep } from "./screens/PlanStep";
import { ProfileStep } from "./screens/ProfileStep";
import { SignIn } from "./screens/SignIn";

const STEP_PATH: Record<Step, string> = {
  PROFILE: "/onboarding/profile",
  KYC: "/onboarding/kyc",
  AGREEMENTS: "/onboarding/agreements",
  PLAN: "/onboarding/plan",
  DONE: "/home",
};
const STEP_INDEX: Record<Step, 0 | 1 | 2 | 3 | 4> = { PROFILE: 0, KYC: 1, AGREEMENTS: 2, PLAN: 3, DONE: 4 };

export function App() {
  const route = useRoute();
  const [signedIn, setSignedIn] = useState(!!getToken());
  const [state, setState] = useState<OnboardingState | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setState(await api<OnboardingState>("/v1/onboarding"));
      setError(null);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        setToken(null);
        setSignedIn(false);
      } else setError("Could not reach Finversify. Check your connection and try again.");
    }
  }, []);

  useEffect(() => {
    if (signedIn) void load();
  }, [signedIn, load]);

  // Keep the URL on the step the account is actually at: no skipping ahead.
  useEffect(() => {
    if (!state) return;
    const wanted = STEP_PATH[state.step];
    const p = route.path;
    const routeStep = (Object.keys(STEP_PATH) as Step[]).find((s) => STEP_PATH[s] === p);
    if (p === "/" || p === "/signin" || (routeStep && STEP_INDEX[routeStep] > STEP_INDEX[state.step])) go(wanted);
    if (p === "/plan" && state.step !== "DONE") go(wanted);
  }, [state, route.path]);

  const signOut = () => {
    setToken(null);
    setSignedIn(false);
    setState(null);
    go("/signin");
  };

  const right = signedIn ? (
    <button className="link" onClick={signOut}>
      Sign out
    </button>
  ) : null;

  if (!signedIn) {
    return (
      <Shell>
        <SignIn
          onSignedIn={() => {
            setSignedIn(true);
            go("/");
          }}
        />
      </Shell>
    );
  }

  if (!state) {
    return (
      <Shell right={right}>
        <div className="column">{error ? <Notice tone="bad">{error}</Notice> : <p className="muted">Loading your account…</p>}</div>
      </Shell>
    );
  }

  const update = (s: OnboardingState) => setState(s);
  const session = route.query.get("session");
  const p = route.path;

  if (p === "/home" && state.step === "DONE") {
    return (
      <Shell right={right}>
        <Home onAddService={(serviceId) => go(serviceId ? `/plan?service=${serviceId}` : "/plan")} />
      </Shell>
    );
  }

  if (p === "/plan" && state.step === "DONE") {
    return (
      <Shell right={right}>
        <div className="column">
          <p>
            <button className="link" onClick={() => go("/home")}>
              Back to my calls
            </button>
          </p>
          <PlanStep key={route.query.get("service") ?? ""} preselect={route.query.get("service")} email={state.profile?.email ?? null} onPaid={() => go("/home")} />
        </div>
      </Shell>
    );
  }

  const index = Math.min(3, STEP_INDEX[state.step]) as 0 | 1 | 2 | 3;
  return (
    <Shell right={right}>
      <div className="column">
        <Steps current={index} />
        {p === "/onboarding/profile" && (
          <ProfileStep
            state={state}
            onSaved={(s) => {
              update(s);
              go(STEP_PATH[s.step]);
            }}
          />
        )}
        {p === "/onboarding/kyc" && (
          <KycStep
            state={state}
            session={session}
            onUpdate={(s) => {
              update(s);
              if (s.step !== "KYC") go(STEP_PATH[s.step]);
            }}
          />
        )}
        {p === "/onboarding/agreements" && (
          <AgreementsStep
            session={session}
            onUpdate={(s) => {
              update(s);
              go(STEP_PATH[s.step]);
            }}
          />
        )}
        {p === "/onboarding/plan" && (
          <PlanStep
            email={state.profile?.email ?? null}
            onPaid={() => {
              void load().then(() => go("/home"));
            }}
          />
        )}
      </div>
    </Shell>
  );
}
